import type { Channel } from "../api/types.js";
import type { Phase } from "../app/end-of-speech.js";
import { el } from "../lib/dom.js";
import { icon } from "../lib/icon.js";
import { reducedMotion } from "../lib/root.js";
import { AIM } from "./aim-gesture.js";
import { haptic, micButton, micState, setMic, setSend } from "./mic.js";

/**
 * The chat dock — composer v2 (UX spec v3 §D, DCTA-88): `[field][8 px][one action
 * button]`. The button is the mic while the field is empty and Send (round, apricot,
 * arrow up) the moment there is text; nothing inside the field but the words.
 * States: idle · typing · listening · processing · disabled; the button's
 * `data-mode`: mic | send.
 *
 * DCTA-141 — voice that listens to the end. Listening has a mode (`data-listen`):
 *  - `tap` (a tap on the mic, the mark's hold, "Say it again"): the slot is a pill that
 *    says **Done** — big and obvious — and the app ends listening by adaptive silence
 *    (`app/end-of-speech.ts`); the phase (`data-phase`) words the label: Listening ·
 *    Still listening · Sending, with a ring that drains around Done in the last second
 *    before an automatic send (new words cancel it);
 *  - `hold` (the mic pressed `AIM.hold` ms): listening while held, "Release to send";
 *    the release is done; the system taking the pointer is never a send. DCTA-181: a press released within
 *    `HOLD_TAP_MS` of its start was a tap held a little long — listening goes on as a tap, with Done.
 * And one more state, **review** (`data-review`, over `typing`): the words wait in the
 * field with a pill that says **Send** — Mdm Lim's end of listening (never an automatic
 * send), a tap on the words, the cap. Words from the microphone are sent as `voice`
 * even after an edit (the bank's voice read-backs still apply); an emptied field drops it.
 *
 * Deviations from the mock-up's markup, all product necessities (README,
 * "Design deviations — v3"):
 *  - the field holds a real `<textarea>` (the mock-up draws spans and a caret);
 *    it grows to `--field-lines` lines, then scrolls inside itself;
 *  - the live region says "Listening" once; the ticking timer and the phase words sit
 *    beside it, `aria-hidden`: a timer inside a live region is read out every second, and
 *    anything spoken while the customer speaks can be heard by the microphone.
 */
export interface ComposerHandlers {
  /** `voice`: the words in the field came from the microphone (review), edited or not. */
  onSend(text: string, channel: Channel): void;
  /** A tap on the mic: listen, tap mode. */
  onMic(): void;
  /** × or Esc while listening: nothing is sent. */
  onCancel(): void;
  /** The press on the mic became a hold: listen while held. */
  onHold(): void;
  /** The held press is over: that is done. */
  onRelease(): void;
  /** The system took the pointer during a hold: listening goes on, tap mode. Never a send. */
  onHoldLost(): void;
  /** Done, tapped. */
  onDone(): void;
  /** DCTA-162 F31: Stop, tapped, while the bank reads aloud. */
  onStopSpeech?(): void;
  /** DCTA-162 F31: the field touched or typed in while the bank reads aloud. */
  onFieldTouch?(): void;
}

export type ListenMode = "tap" | "hold";

const PLACEHOLDER = {
  idle: "Ask or say what to do",
  processing: "One moment",
  // Ruling Q16 (DCTA-139): the microphone shows that it is off; ‘Type what to do · microphone is off’ was cut at 360 px.
  disabled: "Type what to do",
  // §F.2 16g (F13): a question waits for words or a figure — the field asks for the answer.
  answering: "Say or type your answer",
  answeringOff: "Type your answer",
  // P0-4 (DCTA-192): while the bank reads aloud the field sits beside Stop (142 px at 360 in large type): the idle and
  // answering lines do not fit there, and an empty box read as broken in the demo — this one does (90 px).
  speaking: "Type or say",
} as const;
/** The field's accessible name: what it is for now (16g). */
const NAME = { idle: "Ask or say what to do", answering: "Your answer" } as const;

/** DCTA-141's words beside the bars — §F.2 row 19 (19a, 19c, 19d, 19e; DCTA-146: accepted as built). */
const LISTEN = { hearing: "Listening", still: "Still listening", countdown: "Sending", hold: "Release to send" } as const;
/**
 * §F.2 19i (DCTA-146, ask F20; built by DCTA-143): said once in the polite region when the words wait for review the
 * customer did not ask for — Mdm Lim's silence (large type), the 60 s cap, a microphone that stopped and could not
 * restart. What to do now, not why listening stopped (decision 59). Nothing visible is added; never on a tap on the words.
 */
const REVIEW_SAY = "Check your words, then tap Send.";
const DONE = "Done";
const SEND = "Send";

const BARS = 12;
const NS = "http://www.w3.org/2000/svg";
/** The countdown ring hugs the pill this far outside it (px), at this stroke (px). */
const RING_GAP = 4;
const RING_W = 3;
/** After a hold's release, the click the same press makes is not a tap (ms). */
const SWALLOW_MS = 800;
/**
 * DCTA-181 (Umakhan: "tap, no holding"): a press released sooner than this after it began is a tap held a little long,
 * not hold-to-talk — listening goes on as a tap, with Done (the release used to end it before a word came back).
 */
const HOLD_TAP_MS = 1000;

export class Composer {
  readonly element: HTMLElement;
  readonly mic: HTMLButtonElement;
  readonly input: HTMLTextAreaElement;
  /** DCTA-141: Done while listening (tap), Send in review. */
  readonly pill: HTMLButtonElement;
  readonly #handlers: ComposerHandlers;
  readonly #cancel: HTMLButtonElement;
  /** DCTA-162 F31: Stop reading aloud — shown only while the bank speaks. */
  readonly #stop: HTMLButtonElement;
  readonly #bars: HTMLElement[];
  readonly #timer: HTMLElement;
  readonly #label: HTMLElement;
  readonly #announce: HTMLElement;
  readonly #pillWord: HTMLElement;
  readonly #ring: SVGSVGElement;
  readonly #ringPath: SVGPathElement;
  #ringRun: Animation | undefined;
  #micOff = false;
  /** DCTA-139 (F13): the id of the open question's sentence while it waits for words or a figure. */
  #answering: string | undefined;
  /** A turn is in flight (§D.9): Send shows but does not act, and never queues silently. */
  #held = false;
  /** The field holds words from the microphone (review): they go as `voice`, edited or not, until the field is emptied. */
  #voiceWords = false;
  #tick: ReturnType<typeof setInterval> | undefined;
  #press: { readonly id: number; readonly timer: ReturnType<typeof setTimeout>; readonly at: number } | undefined;
  #holding = false;
  #swallowUntil = 0;
  /** Esc is the desktop's cancel while listening (§4), wherever focus is. */
  readonly #onEscape = (event: KeyboardEvent): void => {
    if (event.key === "Escape") {
      event.preventDefault();
      this.#handlers.onCancel();
    }
  };

  constructor(handlers: ComposerHandlers) {
    this.#handlers = handlers;
    this.input = el("textarea", {
      class: "input",
      aria: { label: NAME.idle },
      placeholder: PLACEHOLDER.idle,
      autocomplete: "off",
      data: {},
    }) as HTMLTextAreaElement;
    this.input.rows = 1;
    this.input.setAttribute("enterkeyhint", "send");
    this.input.setAttribute("autocapitalize", "sentences");
    this.input.setAttribute("spellcheck", "false");

    this.#cancel = el("button", { class: "raw cancel", type: "button", aria: { label: "Cancel" } }, [icon("i-x")]);
    // F31: "Stop", named "Stop reading aloud" (WCAG 2.5.3: the name starts with the visible word), 44 px.
    this.#stop = el("button", { class: "raw stop-read", type: "button", hidden: true, aria: { label: "Stop reading aloud" } }, [
      icon("i-stop"),
      el("span", {}, ["Stop"]),
    ]);
    this.#stop.addEventListener("click", () => this.#handlers.onStopSpeech?.());
    this.#bars = Array.from({ length: BARS }, () => el("i"));
    this.#timer = el("span", { class: "timer" }, ["0:00"]);
    this.#label = el("span", { class: "lbl", aria: { hidden: "true" } });
    this.#setLabel(LISTEN.hearing, true);
    this.#announce = el("span", { class: "vis-hidden", aria: { live: "polite" } });
    const live = el("div", { class: "live" }, [
      el("span", { class: "live-dot", aria: { hidden: "true" } }),
      el("span", { class: "bars", aria: { hidden: "true" } }, this.#bars),
      this.#label,
      this.#announce,
    ]);
    this.mic = micButton(undefined, true);
    this.#pillWord = el("span", { class: "pw" }, [DONE]);
    this.#ring = document.createElementNS(NS, "svg");
    this.#ring.setAttribute("class", "cd");
    this.#ring.setAttribute("aria-hidden", "true");
    this.#ring.setAttribute("focusable", "false");
    this.#ringPath = document.createElementNS(NS, "path");
    this.#ring.appendChild(this.#ringPath);
    this.pill = el("button", { class: "raw pill", type: "button", hidden: true }, [this.#pillWord]);
    this.pill.appendChild(this.#ring);
    this.element = el("div", { class: "composer", data: { state: "idle" } }, [
      this.#cancel,
      this.#stop,
      el("div", { class: "field" }, [this.input]),
      live,
      this.mic,
      this.pill,
    ]);

    this.input.addEventListener("input", () => {
      this.#grow();
      if (this.input.value.trim() === "") this.#endReview();
      this.#syncTyping();
    });
    // F31: a tap on the field, or a key in it, while the bank speaks stops the speech (the chat decides).
    const touched = (): void => {
      if (this.element.dataset["speaking"] !== undefined) this.#handlers.onFieldTouch?.();
    };
    this.input.addEventListener("pointerdown", touched);
    this.input.addEventListener("keydown", (event) => {
      touched();
      if (event.key === "Enter" && !event.shiftKey && !event.isComposing) {
        event.preventDefault();
        this.#send();
      }
    });
    // P2-4: pressing Send leaves the focus in the field, so the keyboard stays up (a messenger's Send).
    for (const button of [this.mic, this.pill])
      button.addEventListener("mousedown", (event) => {
        if (this.#sendFace(button) && document.activeElement === this.input) event.preventDefault();
      });
    this.#cancel.addEventListener("click", () => {
      if (this.element.dataset["review"] === "true") this.#clear();
      else this.#handlers.onCancel();
    });
    this.mic.addEventListener("pointerdown", (event) => this.#down(event));
    this.mic.addEventListener("pointerup", (event) => this.#up(event, false));
    this.mic.addEventListener("pointercancel", (event) => this.#up(event, true));
    // One slot, two faces: the face decides what a tap does. Send never starts the microphone.
    this.mic.addEventListener("click", () => {
      if (performance.now() < this.#swallowUntil) {
        this.#swallowUntil = 0;
        return;
      }
      if (this.#sending()) {
        this.#send();
        return;
      }
      if (this.#micOff || micState(this.mic) === "processing") return;
      this.#handlers.onMic();
    });
    this.pill.addEventListener("click", () => {
      if (this.element.dataset["review"] === "true") this.#send();
      else if (this.element.dataset["state"] === "listening") this.#handlers.onDone();
    });
  }

  get value(): string {
    return this.input.value;
  }

  /** Words the app puts in the field (an account's prefill, a card's words to edit): typed words. */
  setValue(text: string): void {
    this.#endReview();
    this.input.value = text;
    this.#grow();
    this.#syncTyping();
  }

  focus(): void {
    this.input.focus();
  }

  /**
   * §F.2 16g (F13): a question that takes words or a figure is open (`questionId`: its sentence's id) — the placeholder
   * asks for the answer, the field is named "Your answer" and described by the question; `undefined`: it ended.
   */
  answering(questionId: string | undefined): void {
    this.#answering = questionId;
    if (questionId === undefined) this.input.removeAttribute("aria-describedby");
    else this.input.setAttribute("aria-describedby", questionId);
    this.input.setAttribute("aria-label", questionId === undefined ? NAME.idle : NAME.answering);
    this.#restPlaceholder();
  }

  /** A bank card arrived: on a touch screen the keyboard goes, so the card and its sign action show whole. */
  dropKeyboard(): void {
    if (globalThis.matchMedia?.("(pointer: coarse)").matches === true && document.activeElement === this.input) this.input.blur();
  }

  listen(mode: ListenMode = "tap"): void {
    const hadFocus = this.#focusInSlot();
    setSend(this.mic, false, this.#held);
    setMic(this.mic, "listening");
    this.element.dataset["listen"] = mode;
    this.element.dataset["phase"] = "waiting";
    this.#pillWord.textContent = DONE;
    this.#cancel.setAttribute("aria-label", "Cancel");
    this.#stopRing();
    document.addEventListener("keydown", this.#onEscape);
    this.#announce.textContent = "Listening";
    this.levels([]);
    if (this.#tick !== undefined) clearInterval(this.#tick);
    let seconds = 0;
    this.#timer.textContent = "0:00";
    this.#tick = setInterval(() => {
      seconds += 1;
      this.#timer.textContent = `${String(Math.floor(seconds / 60))}:${String(seconds % 60).padStart(2, "0")}`;
    }, 1000);
    if (mode === "hold") this.#setLabel(LISTEN.hold, false);
    else this.#setLabel(LISTEN.hearing, true);
    this.#slot();
    // Tap mode hides the mic behind Done: a keyboard's focus goes with it.
    if (hadFocus && mode === "tap") this.pill.focus();
  }

  /** A hold that lost its pointer goes on as tap listening: Done in the slot, the timer where it was. */
  tapMode(): void {
    if (this.element.dataset["state"] !== "listening") return;
    this.element.dataset["listen"] = "tap";
    this.#setLabel(LISTEN.hearing, true);
    this.#slot();
  }

  /**
   * The listening phase (tap mode), for the eye only: Listening (with the timer) · Still listening · Sending — the
   * last one with the ring draining around Done over `left` ms, the time to the automatic send.
   */
  phase(phase: Phase, left = 0): void {
    if (this.element.dataset["state"] !== "listening" || this.element.dataset["listen"] !== "tap") return;
    const was = this.element.dataset["phase"];
    this.element.dataset["phase"] = phase;
    if (phase === "still") this.#setLabel(LISTEN.still, false);
    else if (phase === "countdown") this.#setLabel(LISTEN.countdown, false);
    else this.#setLabel(LISTEN.hearing, true);
    if (phase !== "countdown") this.#stopRing();
    else if (was !== "countdown") this.#drain(left);
  }

  /**
   * The words wait for the customer: in the field, with a pill that says Send. `focus`: editing (a tap on the words —
   * the keyboard comes up and the field reads them); otherwise the app could not hear an end, and 19i says what to do.
   */
  review(text: string, o: { readonly focus: boolean }): void {
    const hadFocus = this.#focusInSlot();
    this.#stopTimer();
    this.#stopRing();
    this.#announce.textContent = o.focus ? "" : REVIEW_SAY;
    this.#endListen();
    setMic(this.mic, this.#micOff ? "disabled" : "idle");
    this.#placeholder(this.#rest());
    this.input.value = text;
    this.#grow();
    this.#voiceWords = true;
    this.element.dataset["review"] = "true";
    this.#pillWord.textContent = SEND;
    this.#cancel.setAttribute("aria-label", "Clear");
    this.#syncTyping();
    if (o.focus) {
      this.input.focus();
      this.input.setSelectionRange(text.length, text.length);
    } else if (hadFocus) this.pill.focus();
  }

  process(): void {
    const hadFocus = this.#focusInSlot();
    this.#stopTimer();
    this.#stopRing();
    this.#endListen();
    this.#announce.textContent = "";
    setMic(this.mic, "processing");
    this.#placeholder(PLACEHOLDER.processing);
    this.#slot();
    if (hadFocus) this.mic.focus();
  }

  idle(): void {
    const hadFocus = this.#focusInSlot();
    this.#stopTimer();
    this.#stopRing();
    this.#endListen();
    this.#announce.textContent = "";
    setMic(this.mic, this.#micOff ? "disabled" : "idle");
    this.#placeholder(this.#rest());
    this.#syncTyping();
    if (hadFocus && this.element.dataset["review"] !== "true") this.mic.focus();
  }

  /**
   * DCTA-162 F31 (spoken.md): the bank is reading `part` aloud — the Stop pill shows in the row, the field and the mic
   * stay (a tap on either stops the speech too); `undefined`: it is quiet again. The part is on the composer
   * (`data-speaking`) for the device notes and the e2e. A keyboard's focus on Stop when it goes moves to the mic.
   */
  speaking(part?: string): void {
    const hadFocus = part === undefined && document.activeElement === this.#stop;
    if (part === undefined) delete this.element.dataset["speaking"];
    else this.element.dataset["speaking"] = part;
    this.#stop.hidden = part === undefined;
    this.#restPlaceholder();
    if (hadFocus) this.mic.focus();
  }

  /**
   * DCTA-181: a status for the screen reader only, in the polite region — nothing visible, never the bank's voice (a
   * repeated miss: the line is not drawn again, but the customer who cannot see it is told). Listening replaces it.
   */
  status(text: string): void {
    this.#announce.textContent = text;
  }

  /** No permission, no engine or offline: the mic is off, typing still works. */
  disable(): void {
    this.#micOff = true;
    this.idle();
  }

  /** A turn is in flight: Send stays on screen but does not act until it returns (§D.9). */
  hold(on: boolean): void {
    this.#held = on;
    this.#syncTyping();
  }

  get micOff(): boolean {
    return this.#micOff;
  }

  /** Input level per bar, 0..1 → 4..24 px (AnalyserNode in `adapters/level.ts`). */
  levels(values: readonly number[]): void {
    this.#bars.forEach((bar, i) => {
      const v = Math.min(1, Math.max(0, values[i] ?? 0));
      bar.style.height = `${String(4 + Math.round(v * 20))}px`;
    });
  }

  // ------------------------------------------------------------- the hold
  #down(event: PointerEvent): void {
    if (!this.#sending() && !this.#micOff) haptic();
    if (event.button !== 0 || this.#sending() || this.#micOff || micState(this.mic) !== "idle") return;
    this.#swallowUntil = 0;
    if (this.#press !== undefined) clearTimeout(this.#press.timer);
    try {
      this.mic.setPointerCapture(event.pointerId);
    } catch {
      /* a synthetic pointer has nothing to capture */
    }
    const id = event.pointerId;
    const timer = setTimeout(() => {
      if (this.#press?.id !== id) return;
      this.#holding = true;
      this.#handlers.onHold();
    }, AIM.hold);
    this.#press = { id, timer, at: performance.now() };
  }

  #up(event: PointerEvent, lost: boolean): void {
    const press = this.#press;
    if (press === undefined || press.id !== event.pointerId) return;
    clearTimeout(press.timer);
    this.#press = undefined;
    // Released before the hold: a tap — the click that follows starts listening.
    if (!this.#holding) return;
    this.#holding = false;
    const now = performance.now();
    this.#swallowUntil = now + SWALLOW_MS;
    // The system took the pointer, or the press was a tap held a little long: listening goes on as a tap.
    if (lost || now - press.at < HOLD_TAP_MS) this.#handlers.onHoldLost();
    else this.#handlers.onRelease();
  }

  // ------------------------------------------------------------- internals
  #sending(): boolean {
    return this.mic.dataset["mode"] === "send";
  }

  #sendFace(button: HTMLElement): boolean {
    return button === this.pill ? this.element.dataset["review"] === "true" : this.#sending();
  }

  /** Focus on a control of the slot (mic, Done/Send, ×) — it follows the slot when the slot changes face. */
  #focusInSlot(): boolean {
    const active = document.activeElement;
    return active === this.mic || active === this.pill || active === this.#cancel;
  }

  #send(): void {
    const text = this.input.value.trim();
    if (text === "" || this.#held) return;
    const fromPill = document.activeElement === this.pill;
    const channel: Channel = this.#voiceWords ? "voice" : "typed";
    this.input.value = "";
    this.#grow();
    this.#endReview();
    this.#syncTyping();
    if (fromPill) this.mic.focus();
    // DCTA-85 ruling P2-4: the keyboard stays up after Send, as in a messenger — a follow-up
    // ("which John?") is typed at once. It drops when a bank card arrives (dropKeyboard).
    this.#handlers.onSend(text, channel);
  }

  /** Clear (the × in review): the words go, the mic is back. */
  #clear(): void {
    const hadFocus = this.#focusInSlot();
    this.input.value = "";
    this.#grow();
    this.#endReview();
    this.#syncTyping();
    if (hadFocus) this.mic.focus();
  }

  #endReview(): void {
    // 19i is said once: the words were sent or cleared.
    if (this.element.dataset["review"] === "true") this.#announce.textContent = "";
    this.#voiceWords = false;
    delete this.element.dataset["review"];
    this.#pillWord.textContent = DONE;
    this.#cancel.setAttribute("aria-label", "Cancel");
  }

  #endListen(): void {
    delete this.element.dataset["listen"];
    delete this.element.dataset["phase"];
  }

  /** The words beside the bars; the timer (and its separator) is decoration the CSS drops where it would not fit. */
  #setLabel(words: string, timer: boolean): void {
    this.#label.replaceChildren(el("b", {}, [words]), ...(timer ? [el("span", { class: "sep" }, [" · "]), this.#timer] : []));
  }

  /**
   * The ring around Done empties over `left` ms, as a timer does: a stadium drawn clockwise from twelve o'clock whose
   * end runs back to twelve (a full static ring under reduced motion: the word "Sending" says it).
   */
  #drain(left: number): void {
    const box = this.pill.getBoundingClientRect();
    const w = box.width + RING_GAP * 2;
    const h = box.height + RING_GAP * 2;
    const x0 = RING_W / 2;
    const y0 = RING_W / 2;
    const x1 = w - RING_W / 2;
    const y1 = h - RING_W / 2;
    const r = Math.max(0, (y1 - y0) / 2);
    const straight = Math.max(0, x1 - x0 - 2 * r);
    const n = (v: number): string => String(Math.round(v * 100) / 100);
    this.#ring.setAttribute("viewBox", `0 0 ${n(w)} ${n(h)}`);
    const path = this.#ringPath;
    path.setAttribute(
      "d",
      `M${n(w / 2)} ${n(y0)} H${n(x1 - r)} A${n(r)} ${n(r)} 0 0 1 ${n(x1 - r)} ${n(y1)} H${n(x0 + r)} A${n(r)} ${n(r)} 0 0 1 ${n(x0 + r)} ${n(y0)} Z`,
    );
    // A stadium's length: its two straight sides and one whole circle of its height.
    const length = 2 * straight + 2 * Math.PI * r;
    path.style.strokeDasharray = String(length);
    path.style.strokeDashoffset = "0";
    this.#ringRun?.cancel();
    this.#ringRun = undefined;
    if (left <= 0 || reducedMotion(document.documentElement) || typeof path.animate !== "function") return;
    this.#ringRun = path.animate([{ strokeDashoffset: "0" }, { strokeDashoffset: String(length) }], { duration: left, easing: "linear", fill: "forwards" });
    // A cancelled run rejects `finished` (new words, Done, Cancel): expected, never an error.
    this.#ringRun.finished.catch(() => undefined);
  }

  #stopRing(): void {
    this.#ringRun?.cancel();
    this.#ringRun = undefined;
    this.#ringPath.style.strokeDashoffset = "0";
  }

  #syncTyping(): void {
    const state = this.element.dataset["state"];
    const typing = this.input.value.trim() !== "";
    // Listening hides the field: the slot is the mic's stop, whatever the field holds.
    setSend(this.mic, typing && state !== "listening", this.#held);
    this.pill.setAttribute("aria-disabled", this.element.dataset["review"] === "true" && this.#held ? "true" : "false");
    if (state !== "listening" && state !== "processing") this.element.dataset["state"] = typing ? "typing" : this.#micOff ? "disabled" : "idle";
    this.#slot();
  }

  /** One action in the slot, never two: the pill (Done while listening to a tap, Send in review) or the mic. */
  #slot(): void {
    const pill = (this.element.dataset["state"] === "listening" && this.element.dataset["listen"] === "tap") || this.element.dataset["review"] === "true";
    this.pill.hidden = !pill;
    this.mic.hidden = pill;
  }

  #placeholder(text: string): void {
    this.input.placeholder = text;
  }

  /** The resting placeholder — unless a turn is in flight, whose "One moment" stays. */
  #restPlaceholder(): void {
    if (this.element.dataset["state"] !== "processing") this.#placeholder(this.#rest());
  }

  /**
   * The placeholder at rest: the microphone on or off, a question waiting for words or not — and, P0-4 (DCTA-192), while
   * the bank speaks beside Stop, the short line that fits there (the microphone-off lines fit as they are).
   */
  #rest(): string {
    if (this.#micOff) return this.#answering === undefined ? PLACEHOLDER.disabled : PLACEHOLDER.answeringOff;
    if (this.element.dataset["speaking"] !== undefined) return PLACEHOLDER.speaking;
    return this.#answering === undefined ? PLACEHOLDER.idle : PLACEHOLDER.answering;
  }

  #grow(): void {
    this.input.style.height = "auto";
    if (this.input.scrollHeight > 0) this.input.style.height = `${String(this.input.scrollHeight)}px`;
  }

  #stopTimer(): void {
    if (this.#tick !== undefined) clearInterval(this.#tick);
    this.#tick = undefined;
    document.removeEventListener("keydown", this.#onEscape);
  }
}
